import React, { useMemo } from "react";
import { Group, Rect, Text as KText, Circle } from "react-konva";
import type { BoostplateOp } from "../types/boostplate";

export interface BoostplateOverlayProps {
  clips: Array<{ elementId?: string; start: number; end: number; [key: string]: any }>;
  playhead: number;
  canvasW: number;
  canvasH: number;
  totalViewportScale: number;
}

interface BoostplateClip {
  start: number;
  end: number;
  name?: string;
  _boostplateId: string;
  _boostplateOps: BoostplateOp[];
  _boostplateSceneIndex: number;
  _boostplateSlotDuration: number;
  _boostplateStrategy: string;
}

// Op category detection (mirrors TimelineTrackRows.tsx:381-384)
function categorizeOps(ops: BoostplateOp[]) {
  const hasZoom = ops.some(op => op.opId?.includes("zoom") || op.opId?.includes("Zoom"));
  const hasTransition = ops.some(op => op.opId?.includes("transition"));
  const hasCamera = ops.some(op => op.opId?.includes("camera") || op.opId?.includes("pan"));
  const hasEffect = ops.some(op => op.opId?.includes("fx") || op.opId?.includes("blur") || op.opId?.includes("glow"));
  const tags: string[] = [];
  if (hasZoom) tags.push("zoom");
  if (hasTransition) tags.push("transition");
  if (hasCamera) tags.push("camera");
  if (hasEffect) tags.push("effects");
  return tags;
}

export function BoostplateOverlay({
  clips,
  playhead,
  canvasW,
  canvasH,
  totalViewportScale,
}: BoostplateOverlayProps) {
  // Filter to only Boostplate placeholder clips
  const bpClips = useMemo(() => {
    return clips
      .filter((c): c is BoostplateClip & typeof c => !!(c as any)._boostplateId)
      .sort((a, b) => (a as any)._boostplateSceneIndex - (b as any)._boostplateSceneIndex);
  }, [clips]);

  // Find active clip at current playhead
  const activeClip = useMemo(() => {
    return bpClips.find(c => playhead >= c.start && playhead < c.end) ?? null;
  }, [bpClips, playhead]);

  // Nothing to show if no Boostplate clips
  if (bpClips.length === 0) return null;

  const totalScenes = bpClips.length;
  const activeIndex = activeClip ? (activeClip as any)._boostplateSceneIndex : -1;
  const s = Math.max(0.001, totalViewportScale);

  // Overlay dimensions (in canvas-space, scaled for zoom)
  const barW = Math.min(canvasW * 0.6, 480);
  const barH = 64 / s;
  const barX = (canvasW - barW) / 2;
  const barY = 16 / s;
  const pad = 10 / s;
  const cornerR = 8 / s;

  // Build text for active scene
  const sceneName = activeClip
    ? (activeClip.name || `Scene ${activeIndex + 1}`)
    : "Boostplate";
  const sceneLabel = activeClip
    ? `Scene ${activeIndex + 1} of ${totalScenes} — ${sceneName}`
    : `${totalScenes} scene${totalScenes !== 1 ? "s" : ""} remaining`;

  // Ops and strategy text
  const opTags = activeClip ? categorizeOps((activeClip as any)._boostplateOps || []) : [];
  const strategy = activeClip ? ((activeClip as any)._boostplateStrategy || "fit") : "";
  const detailText = activeClip
    ? [strategy, ...opTags].filter(Boolean).join(" · ")
    : "";

  // Scene dots
  const dotR = 4 / s;
  const dotGap = 12 / s;
  const dotsStartX = barX + pad;
  const dotsY = barY + barH - pad - dotR;

  return (
    <Group listening={false}>
      {/* Background bar */}
      <Rect
        x={barX}
        y={barY}
        width={barW}
        height={barH}
        fill="rgba(0,0,0,0.75)"
        cornerRadius={cornerR}
        listening={false}
      />

      {/* Scene label */}
      <KText
        x={barX + pad}
        y={barY + pad}
        width={barW - pad * 2}
        text={sceneLabel}
        fontSize={14 / s}
        fontFamily="Inter, system-ui, sans-serif"
        fontStyle="bold"
        fill="#fbbf24"
        listening={false}
      />

      {/* Detail text (strategy + ops) */}
      {detailText && (
        <KText
          x={barX + pad}
          y={barY + pad + 18 / s}
          width={barW - pad * 2}
          text={detailText}
          fontSize={11 / s}
          fontFamily="Inter, system-ui, sans-serif"
          fill="rgba(251,191,36,0.7)"
          listening={false}
        />
      )}

      {/* Scene progress dots */}
      {bpClips.map((clip, i) => {
        const idx = (clip as any)._boostplateSceneIndex;
        const isActive = idx === activeIndex;
        return (
          <Circle
            key={`bp-dot-${idx}`}
            x={dotsStartX + i * dotGap + dotR}
            y={dotsY}
            radius={dotR}
            fill={isActive ? "#fbbf24" : "rgba(161,161,170,0.5)"}
            listening={false}
          />
        );
      })}
    </Group>
  );
}
